import { Observable } from 'rxjs';
import { filter, map, retry, tap } from 'rxjs/operators';

import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { API_RETRY_COUNT, API_RETRY_DELAY, CACHE_TIMEOUT } from '@shared/constants';
import { withAPICache } from '@shared/interceptors';
import { ConfigService } from '@shared/services/core/config.service';
import { InventoryTypesRepository } from '@shared/state/inventory/inventory-types.repository';

@Injectable({ providedIn: 'root' })
export class PricingDataService {
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - constructor
	 **-------------------------------------------------------------------------------------
	 */
	constructor(
		private readonly http: HttpClient,
		private readonly repo: InventoryTypesRepository,
		private readonly env: ConfigService
	) {}
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - getPricingInventoryTypesForCompany
	 * *-------------------------------------------------------------------------------------
	 */
	public getPricingInventoryTypesForCompany(companyId, clearCache = false): Observable<any> {
		const url = `${this.env.get<string>('pricingEndPoint')}/inventory-types`;
		return this.http
			.get(url, {
				context: withAPICache({
					ttl: CACHE_TIMEOUT,
				}),
			})
			.pipe(
				tap(resp => this.repo.add(resp)),
				filter((e: any) => e.companyId == companyId),
				retry({ count: API_RETRY_COUNT, delay: API_RETRY_DELAY })
			);
	}
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - getProductsByInventoryTypeId
	 **-------------------------------------------------------------------------------------
	 */
	public getProductsByInventoryTypeId({ companyId, id }): Observable<any> {
		const url = `${this.env.get('productsEndPoint')}/pricing/products/company/${companyId}/inventory-type/${id}`;
		return this.http
			.get(url, {
				headers: {
					'x-api-key': this.env.get('apiKey'),
				},
			})
			.pipe(map((resp: any) => resp.map(item => item.product_id)));
	}
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - public
	 **-------------------------------------------------------------------------------------
	 */
	public getAvailableProducts({ companyId, productIds, dateRange }): Observable<any> {
		const url = `${this.env.get('reservationsEndPoint')}/public/available-products`;
		return this.http.post(
			url,
			{ companyId, productIds, dateRange },
			{
				headers: {
					'x-api-key': this.env.get('apiKey'),
				},
			}
		);
	}
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - getPricingInventoryTypeById
	 * *-------------------------------------------------------------------------------------
	 */
	public getPricingInventoryTypeById(id: string): Observable<any> {
		const url = `${this.env.get<string>('pricingEndPoint')}/inventory-types/${id}`;
		return this.http.get(url);
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - getPricingInventoryTypeById
	 * *-------------------------------------------------------------------------------------
	 */
	public getBookingPricingInventoryTypeById(id: string): Observable<any> {
		const url = `${this.env.get<string>('pricingEndPoint')}/inventory-types/${id}`;
		return this.http.get(url, {
			headers: {
				company: '102', //this.cacheService.get()
			},
		});
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - getPublicPricingInventoryTypeByCompanyId
	 * *-------------------------------------------------------------------------------------
	 */
	public getPublicPricingInventoryTypeByCompanyId(companyId: number, inventoryTypeId: string): Observable<any> {
		const url = `${this.env.get<string>('pricingEndPoint')}/public/inventory-types/company/${companyId}/inventory-type/${inventoryTypeId}`;
		return this.http.get(url);
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - getDefaultInventoryTypeByProductTypeId
	 * *-------------------------------------------------------------------------------------
	 */
	public getDefaultInventoryTypeByProductTypeId(id: number): Observable<any> {
		const url = `${this.env.get<string>('pricingEndPoint')}/inventory-types/default/product-type/${id}`;
		return this.http.get(url);
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - createPricingInventoryType
	 * *-------------------------------------------------------------------------------------
	 */
	public createPricingInventoryType(data: any): Observable<any> {
		const url = `${this.env.get<string>('pricingEndPoint')}/inventory-types`;
		return this.http.post(url, [data]);
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - createDefaultPricingInventoryTypes
	 * *-------------------------------------------------------------------------------------
	 */
	public createDefaultPricingInventoryTypes(companyId: number): Observable<any> {
		const url = `${this.env.get<string>('pricingEndPoint')}/inventory-types/default/${companyId}`;
		return this.http.post(url, {});
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - updatePricingInventoryType
	 * *-------------------------------------------------------------------------------------
	 */
	public updatePricingInventoryType(id: string, data: any): Observable<any> {
		const url = `${this.env.get<string>('pricingEndPoint')}/inventory-types/${id}`;
		return this.http.put(url, data);
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - deletePricingInventoryType
	 * *-------------------------------------------------------------------------------------
	 */
	public deletePricingInventoryType(id: string): Observable<any> {
		const url = `${this.env.get<string>('pricingEndPoint')}/inventory-types/${id}`;
		return this.http.delete(url);
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - getInventoryFeaturesById
	 * *-------------------------------------------------------------------------------------
	 */
	public getInventoryFeaturesById(id: string): Observable<any> {
		const url = `${this.env.get<string>('pricingEndPoint')}/inventory-types/inventory-features/${id}`;
		return this.http.get(url);
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - getInventoryFeaturesByProductId
	 * *-------------------------------------------------------------------------------------
	 */
	public getInventoryFeaturesByProductId(id: number): Observable<any> {
		const url = `${this.env.get<string>('pricingEndPoint')}/inventory-types/inventory-features/product/${id}`;
		return this.http.get(url);
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - createInventoryFeatures
	 * *-------------------------------------------------------------------------------------
	 */
	public createInventoryFeatures(data: any): Observable<any> {
		const url = `${this.env.get<string>('pricingEndPoint')}/inventory-types/inventory-features`;
		return this.http.post(url, data);
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - updateInventoryFeatures
	 * *-------------------------------------------------------------------------------------
	 */
	public updateInventoryFeatures(id: string, data: any): Observable<any> {
		const url = `${this.env.get<string>('pricingEndPoint')}/inventory-types/inventory-features/${id}`;
		return this.http.put(url, data);
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - deleteInventoryFeatures
	 * *-------------------------------------------------------------------------------------
	 */
	public deleteInventoryFeatures(id: string): Observable<any> {
		const url = `${this.env.get<string>('pricingEndPoint')}/inventory-types/inventory-features/${id}`;
		return this.http.delete(url);
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - deleteInventoryFeaturesByProductId
	 * *-------------------------------------------------------------------------------------
	 */
	public deleteInventoryFeaturesByProductId(id: number): Observable<any> {
		const url = `${this.env.get<string>('pricingEndPoint')}/inventory-types/inventory-features/product/${id}`;
		return this.http.delete(url);
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - getDefaultHolidays
	 * *-------------------------------------------------------------------------------------
	 */
	public getDefaultHolidays(): Observable<any> {
		const url = `${this.env.get<string>('pricingEndPoint')}/holidays/default`;
		return this.http.get(url);
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - getDefaultHolidaysForCompany
	 * *-------------------------------------------------------------------------------------
	 */
	public getDefaultHolidaysForCompany(companyId): Observable<any> {
		const url = `${this.env.get<string>('pricingEndPoint')}/holidays/default/company/${companyId}`;
		return this.http.get(url);
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - getCustomHolidaysForCompany
	 * *-------------------------------------------------------------------------------------
	 */
	public getCustomHolidaysForCompany(): Observable<any> {
		const url = `${this.env.get<string>('pricingEndPoint')}/holidays/custom`;
		return this.http.get(url);
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - getHolidaysForCompany
	 * *-------------------------------------------------------------------------------------
	 */
	public getHolidaysForCompany(): Observable<any> {
		const url = `${this.env.get<string>('pricingEndPoint')}/holidays`;
		return this.http.get(url);
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - getHolidayById
	 * *-------------------------------------------------------------------------------------
	 */
	public getHolidayById(id: string): Observable<any> {
		const url = `${this.env.get<string>('pricingEndPoint')}/holidays/${id}`;
		return this.http.get(url);
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - createCustomHoliday
	 * *-------------------------------------------------------------------------------------
	 */
	public createCustomHoliday(data: any): Observable<any> {
		const url = `${this.env.get<string>('pricingEndPoint')}/holidays`;
		return this.http.post(url, [data]);
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - createDefaultHolidays
	 * *-------------------------------------------------------------------------------------
	 */
	public createDefaultHolidays(): Observable<any> {
		const url = `${this.env.get<string>('pricingEndPoint')}/holidays/default`;
		return this.http.post(url, {});
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - createDefaultHolidaysForCompany
	 * *-------------------------------------------------------------------------------------
	 */
	public createDefaultHolidaysForCompany(companyId): Observable<any> {
		const url = `${this.env.get<string>('pricingEndPoint')}/holidays/default/company/${companyId}`;
		return this.http.post(url, {});
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - updateHoliday
	 * *-------------------------------------------------------------------------------------
	 */
	public updateHoliday(id: string, data: any): Observable<any> {
		const url = `${this.env.get<string>('pricingEndPoint')}/holidays/${id}`;
		return this.http.put(url, data);
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - deleteCustomHoliday
	 * *-------------------------------------------------------------------------------------
	 */
	public deleteCustomHoliday(id: string): Observable<any> {
		const url = `${this.env.get<string>('pricingEndPoint')}/holidays/${id}`;
		return this.http.delete(url);
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - getPricesForInventory
	 * *-------------------------------------------------------------------------------------
	 */
	public getPricesForInventory(id: string): Observable<any> {
		const url = `${this.env.get<string>('pricingEndPoint')}/prices/product/${id}`;
		return this.http.get(url);
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - getPricesForInventoryType
	 * *-------------------------------------------------------------------------------------
	 */
	public getPricesForInventoryType(id: string): Observable<any> {
		const url = `${this.env.get<string>('pricingEndPoint')}/prices/inventory-type/${id}`;
		return this.http.get(url);
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - getInventoryPriceById
	 * *-------------------------------------------------------------------------------------
	 */
	public getInventoryPriceById(id: string): Observable<any> {
		const url = `${this.env.get<string>('pricingEndPoint')}/prices/${id}`;
		return this.http.get(url);
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - getInventoryPriceByProductId
	 * *-------------------------------------------------------------------------------------
	 */
	public getInventoryPriceByProductId(id: number): Observable<any> {
		const url = `${this.env.get<string>('pricingEndPoint')}/prices/product/${id}`;
		return this.http.get(url);
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - createInventoryPrice
	 * *-------------------------------------------------------------------------------------
	 */
	public createInventoryPrice(data: any): Observable<any> {
		const url = `${this.env.get<string>('pricingEndPoint')}/prices`;
		return this.http.post(url, [data]);
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - updateInventoryPrice
	 * *-------------------------------------------------------------------------------------
	 */
	public updateInventoryPrice(id: string, data: any): Observable<any> {
		const url = `${this.env.get<string>('pricingEndPoint')}/prices/${id}`;
		return this.http.put(url, data);
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - updateInventoryPrices
	 * *-------------------------------------------------------------------------------------
	 */
	public updateInventoryPrices(data: any): Observable<any> {
		const url = `${this.env.get<string>('pricingEndPoint')}/prices`;
		return this.http.put(url, data);
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - deleteInventoryPrice
	 * *-------------------------------------------------------------------------------------
	 */
	public deleteInventoryPrice(id: string): Observable<any> {
		const url = `${this.env.get<string>('pricingEndPoint')}/prices/${id}`;
		return this.http.delete(url);
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - deleteInventoryPriceByProductId
	 * *-------------------------------------------------------------------------------------
	 */
	public deleteInventoryPriceByProductId(id: number): Observable<any> {
		const url = `${this.env.get<string>('pricingEndPoint')}/prices/product/${id}`;
		return this.http.delete(url);
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - calculateReservationPricing
	 * *-------------------------------------------------------------------------------------
	 */
	public calculateReservationPricing(data: any): Observable<any> {
		const url = `${this.env.get<string>('pricingEndPoint')}/reservation/calculate`;
		return this.http.post(url, data).pipe(retry({ count: API_RETRY_COUNT, delay: API_RETRY_DELAY }));
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - calculateReservationHighVolumePricing
	 * *-------------------------------------------------------------------------------------
	 */
	public calculateReservationHighVolumePricing(data: any): Observable<any> {
		const url = `${this.env.get<string>('pricingEndPoint')}/reservation/calculate/highvolume`;
		return this.http.post(url, data).pipe(retry({ count: API_RETRY_COUNT, delay: API_RETRY_DELAY }));
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - applyDiscounts
	 * *-------------------------------------------------------------------------------------
	 */
	public applyReservationDiscounts(data: any): Observable<any> {
		const url = `${this.env.get<string>('pricingEndPoint')}/reservation/apply-discounts`;
		return this.http.post(url, data);
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - getPricingAvailability
	 * *-------------------------------------------------------------------------------------
	 */
	public getPricingAvailability(data: {
		dates: { startDate: string; endDate: string };
		times: { startTime: string; endTime: string };
		productId: number;
		reservationPriceTotal: number;
		inventoryTypeId: string;
		durationId: string;
		timeslotId: string;
		quantity: number;
		discountCodes: string[];
	}): Observable<any> {
		const url = `${this.env.get<string>('pricingEndPoint')}/reservation/pricing-availability`;
		return this.http.post(url, data);
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - createTempReservation
	 * *-------------------------------------------------------------------------------------
	 */
	public createTempReservation(data: any): Observable<any> {
		const url = `${this.env.get<string>('pricingEndPoint')}/temp-reservation`;
		return this.http.post(url, data);
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - checkTempReservations
	 * *-------------------------------------------------------------------------------------
	 */
	public checkTempReservations(data: any): Observable<any> {
		const url = `${this.env.get<string>('pricingEndPoint')}/temp-reservations/check-available`;
		return this.http.post(url, data);
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - updateTempReservation
	 * *-------------------------------------------------------------------------------------
	 */
	public updateTempReservation(id: string, data: any): Observable<any> {
		const url = `${this.env.get<string>('pricingEndPoint')}/temp-reservation/${id}`;
		return this.http.put(url, data);
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - deleteTempReservation
	 * *-------------------------------------------------------------------------------------
	 */
	public deleteTempReservation(id: string): Observable<any> {
		const url = `${this.env.get<string>('pricingEndPoint')}/temp-reservation/${id}`;
		return this.http.delete(url);
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - getGlobalDiscountCodes
	 * *-------------------------------------------------------------------------------------
	 */
	public getGlobalDiscountCodes(): Observable<any> {
		const url = `${this.env.get<string>('pricingEndPoint')}/discount-codes/company/global`;
		return this.http.get(url, { context: withAPICache({ ttl: CACHE_TIMEOUT }) });
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - getCompanyDiscountCodes
	 * *-------------------------------------------------------------------------------------
	 */
	public getCompanyDiscountCodes(): Observable<any> {
		const url = `${this.env.get<string>('pricingEndPoint')}/discount-codes`;
		return this.http.get(url, { context: withAPICache({ ttl: CACHE_TIMEOUT }) });
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - getDiscountCodesByProductId
	 * *-------------------------------------------------------------------------------------
	 */
	public getDiscountCodesByProductId(id: number): Observable<any> {
		const url = `${this.env.get<string>('pricingEndPoint')}/discount-codes/product/${id}`;
		return this.http.get(url, { context: withAPICache({ ttl: CACHE_TIMEOUT }) });
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - getDiscountCodesByInventoryTypeId
	 * *-------------------------------------------------------------------------------------
	 */
	public getDiscountCodesByInventoryTypeId(id: string): Observable<any> {
		const url = `${this.env.get<string>('pricingEndPoint')}/discount-codes/inventory-type/${id}`;
		return this.http.get(url, { context: withAPICache({ ttl: CACHE_TIMEOUT }) });
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - createDiscountCode
	 * *-------------------------------------------------------------------------------------
	 */
	public createDiscountCode(data: any): Observable<any> {
		const url = `${this.env.get<string>('pricingEndPoint')}/discount-codes/code`;
		return this.http.post(url, data);
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - createDiscountCodes
	 * *-------------------------------------------------------------------------------------
	 */
	public createDiscountCodes(data: any): Observable<any> {
		const url = `${this.env.get<string>('pricingEndPoint')}/discount-codes/codes`;
		return this.http.post(url, [data]);
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - updateDiscountCode
	 * *-------------------------------------------------------------------------------------
	 */
	public updateDiscountCode(id: string, data: any): Observable<any> {
		const url = `${this.env.get<string>('pricingEndPoint')}/discount-codes/code/${id}`;
		return this.http.put(url, data);
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - updateDiscountCodes
	 * *-------------------------------------------------------------------------------------
	 */
	public updateDiscountCodes(data: any): Observable<any> {
		const url = `${this.env.get<string>('pricingEndPoint')}/discount-codes/codes`;
		return this.http.put(url, [data]);
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - deleteDiscountCode
	 * *-------------------------------------------------------------------------------------
	 */
	public deleteDiscountCode(id: string): Observable<any> {
		const url = `${this.env.get<string>('pricingEndPoint')}/discount-codes/code/${id}`;
		return this.http.delete(url);
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - deleteDiscountCode
	 * *-------------------------------------------------------------------------------------
	 */
	public deleteDiscountCodes(data: string[]): Observable<any> {
		const url = `${this.env.get<string>('pricingEndPoint')}/discount-codes/codes`;
		//return this.http.delete(url, { data });
		return this.http.request('DELETE', url, { body: data });
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - getGlobalAddOns
	 * *-------------------------------------------------------------------------------------
	 */
	public getGlobalAddOns(): Observable<any> {
		const url = `${this.env.get<string>('pricingEndPoint')}/add-ons/company/global`;
		return this.http.get(url);
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - getCompanyAddOns
	 * *-------------------------------------------------------------------------------------
	 */
	public getCompanyAddOns(companyId): Observable<any> {
		const url = `${this.env.get<string>('pricingEndPoint')}/pricing-booking/add-ons/company/${companyId}/global`;
		return this.http.get(url);
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - getAddOnsByProductId
	 * *-------------------------------------------------------------------------------------
	 */
	public getAddOnsByProductId(id: number): Observable<any> {
		const url = `${this.env.get<string>('pricingEndPoint')}/add-ons/product/${id}`;
		return this.http.get(url);
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - getAddOnsByInventoryTypeId
	 * *-------------------------------------------------------------------------------------
	 */
	public getAddOnsByInventoryTypeId(id: string): Observable<any> {
		const url = `${this.env.get<string>('pricingEndPoint')}/add-ons/inventory-type/${id}`;
		return this.http.get(url);
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - createAddOn
	 * *-------------------------------------------------------------------------------------
	 */
	public createAddOn(data: any): Observable<any> {
		const url = `${this.env.get<string>('pricingEndPoint')}/add-ons/add-on`;
		return this.http.post(url, data);
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - createAddOns
	 * *-------------------------------------------------------------------------------------
	 */
	public createAddOns(data: any): Observable<any> {
		const url = `${this.env.get<string>('pricingEndPoint')}/add-ons/add-ons`;
		return this.http.post(url, [data]);
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - updateAddOn
	 * *-------------------------------------------------------------------------------------
	 */
	public updateAddOn(id: string, data: any): Observable<any> {
		const url = `${this.env.get<string>('pricingEndPoint')}/add-ons/add-on/${id}`;
		return this.http.put(url, data);
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - updateAddOns
	 * *-------------------------------------------------------------------------------------
	 */
	public updateAddOns(data: any): Observable<any> {
		const url = `${this.env.get<string>('pricingEndPoint')}/add-ons/add-ons`;
		return this.http.put(url, [data]);
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - deleteAddOn
	 * *-------------------------------------------------------------------------------------
	 */
	public deleteAddOn(id: string): Observable<any> {
		const url = `${this.env.get<string>('pricingEndPoint')}/add-ons/add-on/${id}`;
		return this.http.delete(url);
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - deleteAddOns
	 * *-------------------------------------------------------------------------------------
	 */
	public deleteAddOns(data: string[]): Observable<any> {
		const url = `${this.env.get<string>('pricingEndPoint')}/add-ons/add-ons`;
		//return this.http.delete(url, { data });
		return this.http.request('DELETE', url, { body: data });
	}
}
